import { CommonModule } from '@angular/common';
import { Input, Output, EventEmitter, Component, OnInit, AfterViewInit, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { IgxGridModule } from '@infragistics/igniteui-angular';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DataService } from 'src/app/core/services/data/data.service';
import { QuickSearchResults } from 'src/app/shared/entities/QuickSearchResults';

type SendInvoiceGridRow = QuickSearchResults & {
  selectedForSend: boolean;
  isEmailed: boolean;
  notificationStatus: 'success' | 'error' | null;
};

@Component({
  selector: 'app-send-invoices',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatSelectModule,
    MatCheckboxModule,
    IgxGridModule,
  ],
  templateUrl: './send-invoices.component.html',
  styleUrl: './send-invoices.component.scss',
})
export class SendInvoicesComponent implements OnInit, AfterViewInit {
  private readonly snackBar = inject(MatSnackBar);
  private _quickSearchResults: SendInvoiceGridRow[] = [];
  private latestQuickSearchResultsRequestId: number = 0;
  @Output() quickSearchResultsLoading = new EventEmitter<boolean>();
  @Output() formDisplayed = new EventEmitter<void>();

  invoiceUserComments: string = '';
  isProcessingNotifications: boolean = false;
  currentNotificationName: string = '';
  currentNotificationInvoiceNumber: string = '';
  totalRowsToProcess: number = 0;
  completedRows: number = 0;
  isLoadingData: boolean = false;

  _dataService = inject(DataService);
  _appGeneralState = inject(AppGeneralStateService);
  _destroyRefService = inject(DestroyRef);

  @Input()
  set quickSearchResults(value: QuickSearchResults[]) {
    const requestId = ++this.latestQuickSearchResultsRequestId;

    this.isLoadingData = true;
    this.quickSearchResultsLoading.emit(true);

    // Defer heavy filtering/decorating so tab label spinner can paint first.
    setTimeout(() => {
      if (requestId !== this.latestQuickSearchResultsRequestId) {
        return;
      }

      const emailDeliveryRows = (value ?? []).filter((row) => this.isEmailDelivery(row));
      this._quickSearchResults = this.decorateRows(emailDeliveryRows);
      this.isLoadingData = false;
      this.quickSearchResultsLoading.emit(false);
    });
  }

  get quickSearchResults(): SendInvoiceGridRow[] {
    return this._quickSearchResults;
  }

  isSendingEmails: boolean = false;

  constructor() {
    this.isLoadingData = true;
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    setTimeout(() => this.formDisplayed.emit());
  }

  onSearch(): void {}

  onClear(): void {}

  get selectedToEmail(): number {
    return this.quickSearchResults.filter((row) => row.selectedForSend && !row.isEmailed).length;
  }

  get selectedRows(): number {
    return this.quickSearchResults.filter((row) => row.selectedForSend && !row.isEmailed).length;
  }

  get emailedCount(): number {
    return this.quickSearchResults.filter((row) => row.isEmailed).length;
  }

  get hasSelectedSendRows(): boolean {
    return this.quickSearchResults.some((row) => row.selectedForSend && !row.isEmailed);
  }

  get sendProgressValue(): number {
    if (!this.totalRowsToProcess) {
      return 0;
    }

    return (this.completedRows / this.totalRowsToProcess) * 100;
  }

  onSelectedForSendChange(row: SendInvoiceGridRow, checked: boolean): void {
    row.selectedForSend = checked;
  }

  selectAllToEmail(): void {
    this.quickSearchResults.forEach((row) => {
      if (!row.isEmailed) {
        row.selectedForSend = true;
      }
    });
  }

  clearAllToEmail(): void {
    this.quickSearchResults.forEach((row) => {
      if (!row.isEmailed) {
        row.selectedForSend = false;
      }
    });
  }

  private decorateRows(rows: QuickSearchResults[]): SendInvoiceGridRow[] {
    return rows.map((row) => ({
      ...row,
      selectedForSend: (row as SendInvoiceGridRow).selectedForSend ?? false,
      isEmailed: (row as SendInvoiceGridRow).isEmailed ?? false,
      notificationStatus: (row as SendInvoiceGridRow).notificationStatus ?? null,
    }));
  }

  private isEmailDelivery(row: QuickSearchResults): boolean {
    const rawDeliveryValue =
      (row as any).delivery ?? (row as any).deliveryMethod ?? (row as any).invoiceDeliveryMethod ?? '';

    return String(rawDeliveryValue).trim().toLowerCase() === 'email';
  }

  async onSendInvoices(): Promise<void> {
    const selectedRows = this.quickSearchResults.filter((row) => row.selectedForSend && !row.isEmailed);

    if (!selectedRows.length) {
      return;
    }

    this.isProcessingNotifications = true;
    this.totalRowsToProcess = selectedRows.length;
    this.completedRows = 0;
    let iterationsCompleted = false;

    try {
      for (const row of selectedRows) {
        this.currentNotificationName = row.customerName ?? '';
        this.currentNotificationInvoiceNumber = row.invoiceNumber ?? '';

        const sendSucceeded = await this.onSendInvoice(row.invoiceNumber ?? '');
        row.notificationStatus = sendSucceeded ? 'success' : 'error';
        row.isEmailed = sendSucceeded;
        this.completedRows += 1;

        if (sendSucceeded) {
          row.selectedForSend = false;
        }
      }

      iterationsCompleted = true;
    } finally {
      this.isProcessingNotifications = false;

      if (iterationsCompleted) {
        this.snackBar.open('Finished sending invoice notification emails.', 'Close', {
          duration: 5000,
          panelClass: ['send-invoices-complete-snackbar'],
        });
      }
    }
  }

  async onSendInvoice(invoiceNumber: string): Promise<boolean> {
    try {
      const status = await firstValueFrom(
        this._dataService
          .sendInvoiceNotificationEmailWithComments(invoiceNumber, this.invoiceUserComments)
          .pipe(takeUntilDestroyed(this._destroyRefService)),
      );

      return status;
    } catch {
      return false;
    }
  }
}
